/**
 * Unified event directions used across the platform.
 *
 * Hardcoded fallback for the direction tags shown in:
 *   - Calendar filter (LivesScreen)
 *   - Practice upload form (PracticeUploadModal)
 *   - Live-stream creator (LiveStreamModal)
 *   - Live-practice / room creator (CreateRoomModal)
 *   - Offline event creator (OfflineEventModal)
 *
 * The live list is fetched at runtime via `useDirections` →
 * GET /api/content/directions; this constant is the synchronous
 * first-paint + offline fallback. It also drives `DIRECTION_SET` /
 * `serializeDirections`, so any value the BE later accepts must be
 * mirrored here too (otherwise picker selections get silently dropped
 * on submit).
 *
 * Storage shape is a comma-separated string in the existing `Category` field
 * on lives / videos / rooms. A backend follow-up tracked in
 * docs/tech-tasks/event-directions-multi.md migrates this to a dedicated
 * `Categories: string[]` field; the helpers below are forward-compatible.
 */

export const EVENT_DIRECTIONS = [
  'Медитация',
  'Психология',
  'Философия',
  'Телесные практики',
  'Дыхательные практики',
  'Утренняя практика',
  'Перед сном',
  'Традиции',
  // CORE-98 — broaden the directions catalogue. Mirrors the backend
  // EventDirections.Allowed extension in agape-backend (2026-06-03).
  'Здоровое тело',
  'Музыка',
  'Искусство',
  'Йога',
  'Эмоциональный интеллект',
  'Литература',
  'Питание',
  'Долголетие',
  'Короткая практика',
] as const;

export type EventDirection = typeof EVENT_DIRECTIONS[number];

const DIRECTION_SET = new Set<string>(EVENT_DIRECTIONS);

/**
 * Backwards-compat: rooms / lives / videos created before the
 * «Russian-label-as-id» convention was canonicalised may have stored
 * English keys (`Wellness`, `Yoga`, `Meditation`, …). Map them to the
 * canonical EventDirection on read so the filter chips still surface
 * legacy data — without this, a room saved as `categories: ['Wellness']`
 * silently dropped out of every direction filter.
 */
const ENGLISH_ALIASES: Record<string, EventDirection> = {
  Meditation: 'Медитация',
  Psychology: 'Психология',
  Philosophy: 'Философия',
  // Pre-CORE-98 Yoga collapsed into Body Practices because no dedicated
  // tag existed; now that "Йога" is its own canonical value, preserve
  // legacy reads against the precise tag.
  Yoga: 'Йога',
  Breathwork: 'Дыхательные практики',
  Wellness: 'Утренняя практика',
  'Daily Rituals': 'Перед сном',
  Traditions: 'Традиции',
};

/** Normalise a single raw value to a canonical EventDirection or null. */
function normalizeDirection(value: string): EventDirection | null {
  const trimmed = value.trim();
  if (DIRECTION_SET.has(trimmed)) return trimmed as EventDirection;
  if (Object.prototype.hasOwnProperty.call(ENGLISH_ALIASES, trimmed)) {
    return ENGLISH_ALIASES[trimmed];
  }
  return null;
}

/**
 * Parse the stored `category` string into an ordered list of canonical
 * direction names. Tolerates the legacy CSV format, English aliases
 * (mapped to Russian via `ENGLISH_ALIASES`), and silently drops unknown
 * values.
 */
export function parseDirections(category: string | null | undefined): EventDirection[] {
  if (!category) return [];
  const out: EventDirection[] = [];
  const seen = new Set<EventDirection>();
  for (const raw of category.split(',')) {
    const norm = normalizeDirection(raw);
    if (norm && !seen.has(norm)) {
      seen.add(norm);
      out.push(norm);
    }
  }
  return out;
}

/**
 * Serialize a list of direction names into the comma-joined storage form.
 * Order is preserved; duplicates and unknown values are removed.
 */
export function serializeDirections(directions: readonly string[]): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const d of directions) {
    if (DIRECTION_SET.has(d) && !seen.has(d)) {
      seen.add(d);
      out.push(d);
    }
  }
  return out.join(',');
}

/**
 * Returns true when an event matches the active filter. Empty filter matches
 * everything; otherwise the event must share at least one direction with the
 * selection (OR logic).
 */
export function eventMatchesDirections(
  category: string | null | undefined,
  selected: readonly string[],
): boolean {
  if (selected.length === 0) return true;
  const eventDirs = parseDirections(category);
  if (eventDirs.length === 0) return false;
  return eventDirs.some((d) => selected.includes(d));
}

/**
 * Read directions from an event-like object that may carry either the new
 * `categories: string[]` array or the legacy `category: string` CSV field.
 * Prefers the array when both are present.
 */
export function readEventDirections(
  item: { categories?: string[] | null; category?: string | null } | null | undefined,
): EventDirection[] {
  if (!item) return [];
  if (Array.isArray(item.categories) && item.categories.length > 0) {
    const seen = new Set<EventDirection>();
    const out: EventDirection[] = [];
    for (const d of item.categories) {
      const norm = typeof d === 'string' ? normalizeDirection(d) : null;
      if (norm && !seen.has(norm)) {
        seen.add(norm);
        out.push(norm);
      }
    }
    if (out.length > 0) return out;
  }
  return parseDirections(item.category);
}

/**
 * Filter helper that accepts the full item shape — works during the migration
 * where some endpoints return `categories[]` and others still only return
 * `category` CSV.
 */
export function eventItemMatchesDirections(
  item: { categories?: string[] | null; category?: string | null } | null | undefined,
  selected: readonly string[],
): boolean {
  if (selected.length === 0) return true;
  const dirs = readEventDirections(item);
  if (dirs.length === 0) return false;
  return dirs.some((d) => selected.includes(d));
}

/**
 * Stable per-direction accent colour (deterministic hash → palette), so each
 * direction always shows the same distinct colour — like the web tags.
 */
const DIRECTION_PALETTE = [
  '#8B5CF6', // violet
  '#10B981', // green
  '#3B82F6', // blue
  '#F97316', // orange
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#F59E0B', // amber
  '#84CC16', // lime
  '#EF4444', // red
  '#A855F7', // purple
];

export function directionColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return DIRECTION_PALETTE[h % DIRECTION_PALETTE.length];
}

/**
 * URL-код и английское название направления.
 *
 * Ключ — каноническое значение справочника (оно же id направления на бэкенде,
 * оно же лежит у практики в `category`). `slug` — стабильный ASCII-код: это то,
 * что уходит в URL, чтобы адрес не зависел от отображаемого названия, а
 * `nameEn` — только для показа.
 *
 * Жил в `PracticesScreen`, пока был нужен одному экрану. Переехал сюда для
 * CORE-857: тот же код понадобился помощникам перехода из профиля мастера, а
 * две копии одного соответствия разъезжаются при первом же новом направлении.
 */
export const DIRECTION_META: Record<string, { slug: string; nameEn: string }> = {
  'Медитация': { slug: 'meditation', nameEn: 'Meditation' },
  'Психология': { slug: 'psychology', nameEn: 'Psychology' },
  'Философия': { slug: 'philosophy', nameEn: 'Philosophy' },
  'Телесные практики': { slug: 'body-practices', nameEn: 'Body Practices' },
  'Дыхательные практики': { slug: 'breathwork', nameEn: 'Breathwork' },
  'Утренняя практика': { slug: 'morning-practice', nameEn: 'Morning Practice' },
  'Перед сном': { slug: 'before-sleep', nameEn: 'Before Sleep' },
  'Традиции': { slug: 'traditions', nameEn: 'Traditions' },
  'Здоровое тело': { slug: 'healthy-body', nameEn: 'Healthy Body' },
  'Музыка': { slug: 'music', nameEn: 'Music' },
  'Искусство': { slug: 'art', nameEn: 'Art' },
  'Йога': { slug: 'yoga', nameEn: 'Yoga' },
  'Эмоциональный интеллект': { slug: 'emotional-intelligence', nameEn: 'Emotional Intelligence' },
  'Литература': { slug: 'literature', nameEn: 'Literature' },
  'Питание': { slug: 'nutrition', nameEn: 'Nutrition' },
  'Долголетие': { slug: 'longevity', nameEn: 'Longevity' },
  'Короткая практика': { slug: 'short-practice', nameEn: 'Short Practice' },
};

const DIRECTION_ID_BY_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(DIRECTION_META).map(([id, meta]) => [meta.slug, id]),
);

/**
 * Стабильный код направления для URL и навигации.
 *
 * У направления, которого ещё нет в `DIRECTION_META` (бэкенд завёл новое, а
 * фронт не обновили), кодом остаётся само каноническое значение: экранирует
 * его слой URL, а фильтр не теряется молча. Пара к `directionFromSlug`.
 */
export function directionSlug(direction: string): string {
  return DIRECTION_META[direction]?.slug ?? direction;
}

/**
 * Обратное преобразование: код из адреса → каноническое значение справочника,
 * по которому фильтруется выдача. Значение из `URLSearchParams` приходит уже
 * раскодированным, поэтому здесь только поиск по справочнику.
 *
 * Незнакомый код возвращается как есть — так продолжают открываться ссылки на
 * направления, которых фронт ещё не знает, и старые адреса с каноническим
 * значением в query.
 */
export function directionFromSlug(slug: string | null | undefined): string | null {
  if (!slug) return null;
  const trimmed = slug.trim();
  if (!trimmed) return null;
  return DIRECTION_ID_BY_SLUG[trimmed.toLowerCase()] ?? trimmed;
}
